/**
 * CORE-1202 — «Server returned handshake error: Handshake was canceled».
 *
 * Три хаб-клиента (`useLiveChat`, `ChatApiChannel`, `ChatPresenceHub`) держали
 * по собственной копии одного и того же приёма: `Promise.race([conn.start(),
 * таймаут])`. Приём нужен и остаётся — заблокированный РФ-провайдером WS даёт
 * TCP-соединение и после этого молчит, сам по себе `start()` не отклоняется
 * никогда, и без крайнего срока человек сидит на «Подключаемся…» до конца
 * эфира (CORE-407).
 *
 * Сломано в нём другое: `Promise.race` отдаёт вызывающему ПЕРВЫЙ исход и
 * бросает второй. Когда гонку забирает таймаут, вызывающий по отказу зовёт
 * `conn.stop()`, SignalR отменяет собственный хендшейк и отклоняет тот самый
 * `start()` — а подписчиков у него уже нет. Отказ уходит в
 * `onunhandledrejection`, и ровно этим он в проде и виден: комната 207
 * (`YnExiL`, 04.09.2026), два `rtc_js_unhandled_rejection` с текстом
 * «Server returned handshake error: Handshake was canceled» в 20:01:23 и
 * 20:04:20 UTC у одного участника.
 *
 * Строчка в глобальном обработчике — не косметика. Она безымянная: в ней нет
 * ни хаба, ни попытки, ни того, чем кончилось переподключение, поэтому по ней
 * нельзя отличить «эдж не отдал сокет» от «мы сами отменили свой хендшейк по
 * таймауту». А это разные поломки: первая — про sticky sessions на шлюзе,
 * вторая — наша собственная. `onLateFailure` даёт отказу адрес, чтобы
 * вызывающий назвал его своим именем вместо анонимной строки.
 */

/** Всё, что нужно от соединения: SignalR-хаб подходит как есть. */
export interface HubStartLike {
    start: () => Promise<void>;
}

/**
 * `conn.start()` с крайним сроком.
 *
 * Отдаёт вызывающему то же, что и прежняя гонка: отказ `«<label> connect
 * timeout»`, если сокет не поднялся за `ms`, либо настоящий отказ `start()`,
 * если он пришёл раньше. Разница одна и вся суть в ней: обработчик висит на
 * самом `start()`, а не на результате гонки, поэтому его поздний отказ всегда
 * поймает кто-то, кроме глобального хука.
 *
 * @param onLateFailure вызывается ТОЛЬКО для отказа, опоздавшего к гонке
 *        (обычно — отмена хендшейка нашим же `stop()`). Настоящий отказ
 *        `start()` сюда не приходит: его получил вызывающий.
 */
export function startHubWithTimeout(
    conn: HubStartLike,
    ms: number,
    label: string,
    onLateFailure?: (err: unknown) => void,
): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let timedOut = false;

    const started = conn.start();

    // Тот самый обработчик, которого не было. Вешается ДО гонки и переживает
    // её: `started` с этого момента никогда не остаётся без подписчика.
    started.then(
        () => clearTimeout(timer),
        (err) => {
            clearTimeout(timer);
            if (timedOut) onLateFailure?.(err);
        },
    );

    const deadline = new Promise<void>((_, reject) => {
        timer = setTimeout(() => {
            timedOut = true;
            reject(new Error(`${label} connect timeout`));
        }, ms);
    });

    return Promise.race([started, deadline]);
}
